<script setup lang="ts">
import type { TradingAppAd, TradingAppPlatform } from '~/types/homeTradingApps'
import downloadIcon from '~/assets/icon/download.svg?url'
import arrowRightIcon from '~/assets/icon/arrow-right.svg?url'
import traderArrowIcon from '~/assets/icon/arrow-left.svg?url'
import { resolveExternalUrl } from '~/utils/externalUrl'
import { buildTradingAppDetailUrl, type TradingAppTraderAliasMap } from '~/utils/homeTradingApps'

defineOptions({
  name: 'HomeRecommendationApps',
})

type RecommendationPlatform = TradingAppPlatform
type RecommendationSelection = RecommendationPlatform

/** 广告接口在不同版本中字段可能缺失，组件只依赖这些可选展示字段。 */
type RecommendationAd = {
  adId?: string | number
  softwareId?: string | number
  marketId?: string | number
  appBundleId?: string | number
  appName?: string
  appTitle?: string
  appLogo?: string
  screenshots?: readonly string[] | string
  downloadNum?: string | number
  sizes?: string | number
  traderCode?: string | number
  traderName?: string
  traderIco?: string
  [key: string]: unknown
}

/** 推荐位支持对象、二维数组以及 invoke-v3 返回的嵌套结构。 */
type RecommendationInput = unknown
type ParsedPosition = {
  id: string
  title: string
  logo: string
  index: number
  hasExplicitId: boolean
  genericAds: NormalizedAd[]
  iosAds: NormalizedAd[]
  androidAds: NormalizedAd[]
}
type NormalizedAd = RecommendationAd & {
  key: string
  screenshots: string[]
}
type RecommendationTab = {
  value: RecommendationSelection
  label: string
}
type RecommendationModule = {
  id: string
  title: string
  logo: string
  /** 当前推荐位的应用总数，用于标题右侧展示排行总数。 */
  rankTotal: number
  iosAds: NormalizedAd[]
  androidAds: NormalizedAd[]
  visibleAds: NormalizedAd[]
  tabs: RecommendationTab[]
  selection: RecommendationSelection
  kind: 'single' | 'double' | 'many'
  /** 三条带截图的推荐位对应 Figma 的横向截图卡布局。 */
  showGallery: boolean
  rows: NormalizedAd[][]
  domId: string
}
type ScreenshotDragState = {
  container: HTMLElement | null
  captureTarget: HTMLElement | null
  pointerId: number
  startX: number
  scrollLeft: number
  moved: boolean
}

const PLATFORM_OPTIONS: ReadonlyArray<{ value: RecommendationPlatform; name: 'IOS' | 'Android' }> = [
  { value: 1, name: 'IOS' },
  { value: 2, name: 'Android' },
]
const MAX_MANY_ITEMS = 9

const props = withDefaults(
  defineProps<{
    /** iOS 推荐模块；外层数组的每个元素对应一个首页推荐模块。 */
    iosRecommendations?: readonly RecommendationInput[]
    /** Android 推荐模块；外层数组的每个元素对应一个首页推荐模块。 */
    androidRecommendations?: readonly RecommendationInput[]
    /** 兼容迁移期间传入的已合并二维推荐数据。 */
    recommendations?: readonly RecommendationInput[]
    /** 推荐位没有名称时的标题。 */
    title?: string
    /** 两个平台都有数据时使用的默认平台。 */
    defaultPlatform?: RecommendationPlatform
    /** 交易商编码到主站品牌别名的映射，用于「关联交易所」跳转交易所详情页。 */
    traderAliases?: Readonly<TradingAppTraderAliasMap>
  }>(),
  {
    iosRecommendations: () => [],
    androidRecommendations: () => [],
    recommendations: () => [],
    title: '加密交易应用',
    defaultPlatform: 1,
    traderAliases: () => ({}),
  },
)

const emit = defineEmits<{
  'platform-change': [platform: RecommendationSelection, positionId: string]
  click: [ad: RecommendationAd, positionId: string]
}>()

const { locale, t } = useAppI18n()
const { lock: lockDialogScroll, unlock: unlockDialogScroll } = useStickyDialogScrollLock()
const matomo = useMatomo()
const nuxtLinkComponent = resolveComponent('NuxtLink')

/** WikiBit 主站 origin，交易所详情页由主站承接。 */
const WIKIBIT_MAIN_ORIGIN = 'https://www.wikibit.com'

/** 单个交易所整体内容的 hover 阴影，与 WikiBit 主站交易商卡片保持一致。 */
const TRADER_CARD_HOVER_SHADOW = 'transition-shadow hover:shadow-[0_12px_32px_#08133e14]'

const activeSelections = ref<Record<string, RecommendationSelection>>({})
const previewImageUrl = ref('')
const previewImageAlt = ref('')
const previewCloseButton = ref<HTMLButtonElement | null>(null)
const previousFocusedElement = ref<HTMLElement | null>(null)
const suppressNextScreenshotClick = ref(false)
const screenshotDragState: ScreenshotDragState = {
  container: null,
  captureTarget: null,
  pointerId: -1,
  startX: 0,
  scrollLeft: 0,
  moved: false,
}

const normalizedModules = computed(() => buildModules())
const isPreviewOpen = computed(() => Boolean(previewImageUrl.value))
const moduleViews = computed<RecommendationModule[]>(() => {
  return normalizedModules.value.map((module, moduleIndex) => {
    const allAds = deduplicateAds([...module.genericAds, ...module.iosAds, ...module.androidAds])
    const tabs = createTabs(allAds, module.iosAds, module.androidAds, props.defaultPlatform)
    const selection = resolveSelection(module.id, tabs, props.defaultPlatform)
    const visibleAds = getVisibleAds(selection, allAds, module.iosAds, module.androidAds)

    return {
      id: module.id,
      title: module.title || props.title,
      logo: module.logo,
      rankTotal: allAds.length,
      iosAds: module.iosAds,
      androidAds: module.androidAds,
      visibleAds,
      tabs,
      selection,
      kind: getLayoutKind(visibleAds.length),
      showGallery: visibleAds.length === 3 && visibleAds.every((ad) => getScreenshots(ad).length > 0),
      rows: createRows(visibleAds),
      domId: `${moduleIndex}-${sanitizeId(module.id)}`,
    }
  })
})

watch(
  normalizedModules,
  (modules) => {
    const nextSelections: Record<string, RecommendationSelection> = {}

    for (const module of modules) {
      const allAds = deduplicateAds([...module.genericAds, ...module.iosAds, ...module.androidAds])
      const tabs = createTabs(allAds, module.iosAds, module.androidAds, props.defaultPlatform)
      const current = activeSelections.value[module.id]
      nextSelections[module.id] =
        current && tabs.some((tab) => tab.value === current)
          ? current
          : resolveDefaultSelection(tabs, props.defaultPlatform)
    }

    activeSelections.value = nextSelections
  },
  { deep: true, immediate: true },
)

watch(
  () => props.defaultPlatform,
  (platform) => {
    const nextSelections = { ...activeSelections.value }

    for (const module of normalizedModules.value) {
      const allAds = deduplicateAds([...module.genericAds, ...module.iosAds, ...module.androidAds])
      const tabs = createTabs(allAds, module.iosAds, module.androidAds, platform)
      if (tabs.some((tab) => tab.value === platform)) nextSelections[module.id] = platform
    }

    activeSelections.value = nextSelections
  },
)

watch(isPreviewOpen, (open) => {
  if (open) {
    lockDialogScroll()
    void nextTick(() => previewCloseButton.value?.focus())
    return
  }

  unlockDialogScroll()
  void nextTick(() => previousFocusedElement.value?.focus())
  previousFocusedElement.value = null
})

onMounted(() => {
  window.addEventListener('keydown', handlePreviewKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handlePreviewKeydown)
  unlockDialogScroll()
})

/**
 * 从平台数组中建立模块。
 *
 * 稳定 positionId 优先用于跨平台合并；二维接口生成的 position-{index}
 * 只是数组槽位标识，因此在一侧缺少 ID 时按外层索引配对。
 */
function buildModules(): ParsedPosition[] {
  const aliasModules = parseInputModules(props.recommendations, undefined)
  const iosModules = parseInputModules(props.iosRecommendations, 1)
  const androidModules = parseInputModules(props.androidRecommendations, 2)
  const positions: ParsedPosition[] = []

  const appendModules = (modules: ParsedPosition[]) => {
    const matchedPositions = new Set<ParsedPosition>()

    for (const module of modules) {
      const existing =
        (module.hasExplicitId &&
          positions.find(
            (candidate) => !matchedPositions.has(candidate) && candidate.hasExplicitId && candidate.id === module.id,
          )) ||
        positions.find(
          (candidate) =>
            !matchedPositions.has(candidate) &&
            candidate.index === module.index &&
            (!candidate.hasExplicitId || !module.hasExplicitId),
        )

      if (!existing) {
        const nextPosition = {
          ...module,
          genericAds: [...module.genericAds],
          iosAds: [...module.iosAds],
          androidAds: [...module.androidAds],
        }
        positions.push(nextPosition)
        matchedPositions.add(nextPosition)
        continue
      }

      existing.title ||= module.title
      existing.logo ||= module.logo
      existing.genericAds.push(...module.genericAds)
      existing.iosAds.push(...module.iosAds)
      existing.androidAds.push(...module.androidAds)
      // 一个侧面有明确 ID 时，合并结果沿用该 ID，便于稳定 aria/id 和事件参数。
      if (!existing.hasExplicitId && module.hasExplicitId) {
        existing.id = module.id
        existing.hasExplicitId = true
      }
      matchedPositions.add(existing)
    }
  }

  appendModules(aliasModules)
  appendModules(iosModules)
  appendModules(androidModules)

  return positions
}

/** 将任意输入数组解析为外层推荐模块，保留二维数组的每个外层元素。 */
function parseInputModules(input: readonly RecommendationInput[] | undefined, platform?: RecommendationPlatform) {
  const modules = unwrapModuleArray(input)
  if (!modules.length) return []

  // 平面广告数组是旧接口的一种形式，应视作一个模块，而不是 N 个模块。
  if (modules.every((item) => isAdRecord(item))) {
    return [parsePosition(modules, 0, platform)]
  }

  return modules.map((module, index) => parsePosition(module, index, platform))
}

/** 解开 invoke-v3 外层包装，但不会吞掉二维数组的模块边界。 */
function unwrapModuleArray(input: readonly RecommendationInput[] | undefined): unknown[] {
  if (!Array.isArray(input)) return []
  if (input.length === 1 && !Array.isArray(input[0]) && !isAdRecord(input[0])) {
    const nested = findArray(input[0])
    if (nested) return nested
  }
  return [...input]
}

/** 在常见 invoke-v3 字段中寻找数组。 */
function findArray(value: unknown, depth = 0): unknown[] | null {
  if (depth > 6) return null
  if (Array.isArray(value)) return value
  const record = toRecord(value)
  if (!record) return null

  let emptyArray: unknown[] | null = null
  for (const candidate of [
    record.result,
    record.data,
    record.advertisement,
    record.apirecommendationadlist,
    record['advertisement$apirecommendationadlist'],
  ]) {
    const nested = findArray(candidate, depth + 1)
    if (!nested) continue
    if (nested.length > 0) return nested
    emptyArray ||= nested
  }

  return emptyArray
}

/** 将一个外层模块转换为统一的推荐位结构。 */
function parsePosition(value: unknown, index: number, platform?: RecommendationPlatform): ParsedPosition {
  const fallbackId = `position-${index}`

  if (Array.isArray(value)) {
    const ads = normalizeAds(value, `${index}`)
    return {
      id: fallbackId,
      title: '',
      logo: '',
      index,
      hasExplicitId: false,
      genericAds: platform ? [] : ads,
      iosAds: platform === 1 ? ads : [],
      androidAds: platform === 2 ? ads : [],
    }
  }

  const record = toRecord(value)
  if (!record) {
    return {
      id: fallbackId,
      title: '',
      logo: '',
      index,
      hasExplicitId: false,
      genericAds: [],
      iosAds: [],
      androidAds: [],
    }
  }

  const explicitId = readText(record.positionId, record.id)
  const id = explicitId || fallbackId
  const title = readText(record.positionName, record.title, record.name)
  const logo = readText(record.positionLogo, record.logo)
  const directIos = normalizeAds(record.iosAds, `${index}-ios`)
  const directAndroid = normalizeAds(record.androidAds, `${index}-android`)
  const directAds = normalizeAds(record.ads ?? record.adList ?? record.list ?? record.items, `${index}`)

  return {
    id,
    title,
    logo,
    index,
    // position-{index} is generated by the normalizer and represents an array
    // slot, so it remains eligible for index-based cross-platform pairing.
    hasExplicitId: Boolean(explicitId && explicitId !== fallbackId),
    genericAds: platform ? [] : directAds,
    iosAds: directIos.length > 0 ? directIos : platform === 1 ? directAds : [],
    androidAds: directAndroid.length > 0 ? directAndroid : platform === 2 ? directAds : [],
  }
}

/** 递归展开 ads，兼容二维数组和模块对象嵌套。 */
function normalizeAds(value: unknown, keyPrefix: string): NormalizedAd[] {
  if (!Array.isArray(value)) return []

  const ads: NormalizedAd[] = []
  value.forEach((item, index) => {
    const itemKey = `${keyPrefix}-${index}`
    if (Array.isArray(item)) {
      ads.push(...normalizeAds(item, itemKey))
      return
    }

    const record = toRecord(item)
    if (!record) return
    if (!hasAdIdentity(record)) {
      for (const nestedKey of ['ads', 'adList', 'list', 'items']) {
        if (Array.isArray(record[nestedKey])) {
          ads.push(...normalizeAds(record[nestedKey], itemKey))
          return
        }
      }
    }
    const ad = normalizeAd(record, itemKey)
    if (ad) ads.push(ad)
  })

  return ads
}

/** 将接口广告字段映射为卡片所需的稳定字段。 */
function normalizeAd(record: Record<string, unknown>, key: string): NormalizedAd | null {
  if (!hasAdIdentity(record)) return null

  return {
    ...(record as RecommendationAd),
    key: readText(record.adId, record.softwareId, record.appBundleId) || key,
    appName: readText(record.appName, record.name),
    appTitle: readText(record.appTitle, record.title, record.description),
    appLogo: readText(record.appLogo, record.logo, record.icon),
    screenshots: normalizeScreenshots(record.screenshots ?? record.images ?? record.appScreenshots),
    traderName: readText(record.traderName, record.brokerName),
    traderIco: readText(record.traderIco, record.traderIcon, record.brokerIcon),
    downloadNum: readPrimitive(record.downloadNum, record.downloadCount),
    sizes: readPrimitive(record.sizes, record.size),
  }
}

/** 至少存在一个软件身份字段时才渲染广告。 */
function hasAdIdentity(record: Record<string, unknown>): boolean {
  return Boolean(readText(record.appName, record.appLogo, record.softwareId, record.adId, record.appBundleId))
}

/** 判断平面输入是否由广告对象组成，而不是推荐位对象。 */
function isAdRecord(value: unknown): boolean {
  const record = toRecord(value)
  return Boolean(record && hasAdIdentity(record) && !('ads' in record) && !('positionId' in record))
}

/** 读取第一个可用于展示的字符串或数字。 */
function readPrimitive(...values: unknown[]): string | number | undefined {
  return values.find((value): value is string | number => typeof value === 'string' || typeof value === 'number')
}

/** 规范化文本，避免模板出现 undefined。 */
function readText(...values: unknown[]): string {
  const value = values.find((item) => typeof item === 'string' || typeof item === 'number')
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString()
  return ''
}

/** 将未知对象转换为可安全读取的记录。 */
function toRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** 兼容截图数组、JSON 字符串和单个图片地址。 */
function normalizeScreenshots(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => (Array.isArray(item) ? normalizeScreenshots(item) : [item]))
      .filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
      .map((item) => item.trim())
  }
  if (typeof value !== 'string' || !value.trim()) return []

  try {
    const parsed = JSON.parse(value) as unknown
    if (Array.isArray(parsed)) return normalizeScreenshots(parsed)
  } catch {
    // 非 JSON 文本按单个图片地址处理。
  }

  return [value.trim()]
}

/** 合并平台广告时按稳定身份去重，但保留平台自身顺序。 */
function deduplicateAds(ads: NormalizedAd[]): NormalizedAd[] {
  const seen = new Set<string>()
  return ads.filter((ad) => {
    const identity = readText(ad.adId, ad.softwareId, ad.appBundleId, ad.appName) || ad.key
    if (seen.has(identity)) return false
    seen.add(identity)
    return true
  })
}

/** 根据数量选择 Figma 的单条、双条或三列布局。 */
function getLayoutKind(count: number): RecommendationModule['kind'] {
  if (count === 1) return 'single'
  if (count === 2) return 'double'
  return 'many'
}

/** 根据当前数据生成有内容的平台标签，不再提供合并视图。 */
function createTabs(
  allAds: NormalizedAd[],
  iosAds: NormalizedAd[],
  androidAds: NormalizedAd[],
  fallbackPlatform: RecommendationPlatform,
): RecommendationTab[] {
  const tabs = PLATFORM_OPTIONS.flatMap((platform) => {
    const platformAds = platform.value === 1 ? iosAds : androidAds
    if (!platformAds.length) return []

    return [
      {
        value: platform.value,
        label: t('89336', ['{0}']).replace('{0}', platform.name),
      },
    ]
  })

  if (tabs.length || !allAds.length) return tabs

  const fallback = PLATFORM_OPTIONS.find((platform) => platform.value === fallbackPlatform) ?? PLATFORM_OPTIONS[0]
  return fallback ? [{ value: fallback.value, label: t('89336', ['{0}']).replace('{0}', fallback.name) }] : []
}

/** 当前选择无效时优先使用访问设备偏好的平台，否则回退到第一个可用平台。 */
function resolveDefaultSelection(
  tabs: RecommendationTab[],
  preferredPlatform: RecommendationPlatform,
): RecommendationSelection {
  return tabs.find((tab) => tab.value === preferredPlatform)?.value ?? tabs[0]?.value ?? preferredPlatform
}

/** 保留用户已选平台；平台数据变化后自动回退。 */
function resolveSelection(
  id: string,
  tabs: RecommendationTab[],
  preferredPlatform: RecommendationPlatform,
): RecommendationSelection {
  const current = activeSelections.value[id]
  if (current && tabs.some((tab) => tab.value === current)) return current
  return resolveDefaultSelection(tabs, preferredPlatform)
}

/** 根据标签取得当前模块的应用列表。 */
function getVisibleAds(
  selection: RecommendationSelection,
  allAds: NormalizedAd[],
  iosAds: NormalizedAd[],
  androidAds: NormalizedAd[],
): NormalizedAd[] {
  if (selection === 1 && iosAds.length) return iosAds
  if (selection === 2 && androidAds.length) return androidAds
  return allAds
}

/** 三列布局最多展示九项，并按行横向分组，填满当前行后再开始下一行。 */
function createRows(ads: NormalizedAd[]): NormalizedAd[][] {
  const visibleAds = ads.slice(0, MAX_MANY_ITEMS)
  return Array.from({ length: Math.ceil(visibleAds.length / 3) }, (_, rowIndex) =>
    visibleAds.slice(rowIndex * 3, rowIndex * 3 + 3),
  )
}

/** 将模块 ID 转换为稳定的 DOM 标识。 */
function sanitizeId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/gu, '-')
}

/** 使用标准 Tabs 键盘行为切换平台。 */
function handleTabKeydown(event: KeyboardEvent, module: RecommendationModule) {
  if (module.tabs.length < 2) return
  const currentIndex = module.tabs.findIndex((tab) => tab.value === module.selection)
  if (currentIndex < 0) return

  let nextIndex = currentIndex
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % module.tabs.length
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
    nextIndex = (currentIndex - 1 + module.tabs.length) % module.tabs.length
  else if (event.key === 'Home') nextIndex = 0
  else if (event.key === 'End') nextIndex = module.tabs.length - 1
  else return

  event.preventDefault()
  const nextTab = module.tabs[nextIndex]
  if (nextTab) handleSelectionChange(module, nextTab.value)
  const buttons = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>(
    '[role="tab"]',
  )
  buttons?.[nextIndex]?.focus()
}

/** 切换单个模块的平台视图。 */
function handleSelectionChange(module: RecommendationModule, selection: RecommendationSelection) {
  if (!module.tabs.some((tab) => tab.value === selection) || module.selection === selection) return
  activeSelections.value = { ...activeSelections.value, [module.id]: selection }
  emit('platform-change', selection, module.id)
}

/** 构造详情页外链；缺少关键字段时返回空字符串。 */
function getAdHref(ad: RecommendationAd): string {
  return buildTradingAppDetailUrl(ad as TradingAppAd, locale)
}

/** 仅对有效详情地址挂载 NuxtLink 外链属性。 */
function getAdLinkProps(href: string) {
  if (!href) return {}
  return { to: href, external: true, target: '_blank', rel: 'noopener noreferrer' }
}

/** 记录推荐点击并同步给父组件。 */
function handleAdClick(ad: RecommendationAd, positionId: string, event?: MouseEvent) {
  if (event?.target instanceof HTMLElement && event.target.closest('button')) return
  if (!getAdHref(ad)) return
  matomo.trackEvent('af_Trading_Recommend', {})
  emit('click', ad, positionId)
}

/** 构造关联交易所详情地址；缺少品牌别名时返回空字符串。 */
function getTraderHref(ad: RecommendationAd): string {
  const traderCode = readText(ad.traderCode)
  const alias = traderCode ? readText(props.traderAliases[traderCode]) : ''
  if (!alias) return ''

  // 交易所详情页由主站承接，路径不在本站路由表内，这里按主站规则补齐语言段。
  return resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, `/exchanges/${encodeURIComponent(alias)}`, locale)
}

/** 点击「关联交易所」时在主站新窗口打开对应交易所详情页。 */
async function handleTraderClick(ad: RecommendationAd) {
  const href = getTraderHref(ad)
  if (!href) return

  await navigateTo(href, {
    external: true,
    open: {
      target: '_blank',
    },
  })
}

/** 将下载量格式化为千位分隔文本。 */
function formatDownloadNum(value: unknown): string {
  const text = readText(value)
  const numberValue = Number(text.replace(/,/gu, ''))
  return text && Number.isFinite(numberValue) ? numberValue.toLocaleString('en-US') : text || '-'
}

/** 返回已清理的截图地址。 */
function getScreenshots(ad: RecommendationAd): string[] {
  if (Array.isArray(ad.screenshots)) {
    return ad.screenshots.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
  }
  return normalizeScreenshots(ad.screenshots)
}

/** 开始拖动截图条，保留触屏纵向滚动能力。 */
function handleScreenshotPointerDown(event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  const container = event.currentTarget as HTMLElement
  screenshotDragState.container = container
  screenshotDragState.pointerId = event.pointerId
  screenshotDragState.startX = event.clientX
  screenshotDragState.scrollLeft = container.scrollLeft
  screenshotDragState.moved = false
  suppressNextScreenshotClick.value = false
  screenshotDragState.captureTarget = event.target instanceof HTMLElement ? event.target : container
  container.style.cursor = 'grabbing'
  screenshotDragState.captureTarget.setPointerCapture(event.pointerId)
}

/** 根据指针位移滚动截图条，并在明显移动时阻止默认点击。 */
function handleScreenshotPointerMove(event: PointerEvent) {
  const { container, pointerId, startX, scrollLeft } = screenshotDragState
  if (!container || event.pointerId !== pointerId) return

  const distance = event.clientX - startX
  if (Math.abs(distance) > 4) {
    screenshotDragState.moved = true
    event.preventDefault()
  }
  container.scrollLeft = scrollLeft - distance
}

/** 结束拖动，消费紧随其后的 click，避免误开预览。 */
function handleScreenshotPointerEnd(event: PointerEvent, cancelled = false) {
  const { container, pointerId, moved } = screenshotDragState
  if (!container || event.pointerId !== pointerId) return

  suppressNextScreenshotClick.value = !cancelled && moved
  container.style.cursor = ''
  if (screenshotDragState.captureTarget?.hasPointerCapture(pointerId)) {
    screenshotDragState.captureTarget.releasePointerCapture(pointerId)
  }
  screenshotDragState.container = null
  screenshotDragState.captureTarget = null
  screenshotDragState.pointerId = -1
  screenshotDragState.moved = false
}

/** 点击截图打开大图；拖动后产生的 click 会被忽略一次。 */
function handleScreenshotClick(screenshot: string, ad: RecommendationAd) {
  if (suppressNextScreenshotClick.value) {
    suppressNextScreenshotClick.value = false
    return
  }

  previousFocusedElement.value = document.activeElement instanceof HTMLElement ? document.activeElement : null
  previewImageUrl.value = screenshot
  previewImageAlt.value = `${readText(ad.appName) || '应用'} 应用截图`
}

/** 关闭预览并清空图片状态。 */
function closeImagePreview() {
  previewImageUrl.value = ''
  previewImageAlt.value = ''
}

/** 支持 Escape 关闭预览与 Tab 焦点循环。 */
function handlePreviewKeydown(event: KeyboardEvent) {
  if (!isPreviewOpen.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    closeImagePreview()
    return
  }
  if (event.key === 'Tab') {
    event.preventDefault()
    previewCloseButton.value?.focus()
  }
}
</script>

<template>
  <div
    v-if="moduleViews.length"
    class="flex w-full min-w-0 flex-col gap-[12px] font-['PingFang_SC','Microsoft_YaHei',sans-serif]"
  >
    <section
      v-for="module in moduleViews"
      :key="module.id"
      class="flex w-full min-w-0 flex-col"
      :class="module.kind === 'many' ? 'gap-[8px] p-[0px]' : 'gap-[0px] py-[24px]'"
      :aria-labelledby="`recommendation-apps-title-${module.domId}`"
    >
      <header
        class="flex w-full min-w-0 items-center justify-start gap-[8px]"
        :class="module.kind === 'many' ? '' : 'pb-[16px]'"
      >
        <h2
          :id="`recommendation-apps-title-${module.domId}`"
          class="min-w-0 flex-1 truncate text-[24px] leading-[32px] font-[600] text-[#292A2E]"
        >
          {{ module.title }}
        </h2>
        <span
          v-if="module.rankTotal > 0"
          class="group flex shrink-0 cursor-pointer items-center gap-[4px]"
          aria-hidden="true"
        >
          <span
            class="flex h-[18px] min-w-[18px] items-center justify-center rounded-[999px] bg-[#F8F8F8] px-[4px] text-[14px] leading-[18px] font-[400] text-[#7D818A] group-hover:bg-[rgba(24,104,219,0.06)] group-hover:text-[#292A2E]"
          >
            {{ module.rankTotal }}
          </span>
          <CommonImage :src="arrowRightIcon" alt="" :width="20" :height="20" loading="eager" class="rtl:rotate-180" />
        </span>
      </header>

      <div v-if="module?.tabs?.length > 1" class="relative w-full">
        <div
          class="no-scrollbar flex h-[60px] w-full items-center gap-[8px] overflow-x-auto overflow-y-hidden overscroll-x-contain py-[12px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
          :aria-label="`${module.title}`"
        >
          <button
            v-for="tab in module.tabs"
            :id="`recommendation-apps-tab-${module.domId}-${String(tab.value)}`"
            :key="String(tab.value)"
            type="button"
            role="tab"
            class="h-[36px] shrink-0 cursor-pointer rounded-[2px] border-[0.5px] px-[12px] py-[8px] text-[13px] leading-[20px]"
            :class="
              tab.value === module.selection
                ? 'border-transparent bg-[#112D73] font-[500] text-white hover:bg-[#0E255F]'
                : 'border-[rgba(11,18,40,0.14)] bg-white font-[400] text-[#292A2E] hover:bg-[#f2f3f5]/80 hover:text-[#1d2129]/80'
            "
            :aria-selected="tab.value === module.selection"
            :aria-controls="`recommendation-apps-panel-${module.domId}`"
            :tabindex="tab.value === module.selection ? 0 : -1"
            @click="handleSelectionChange(module, tab.value)"
            @keydown="handleTabKeydown($event, module)"
          >
            {{ tab.label }}
          </button>
        </div>
        <span
          aria-hidden="true"
          class="pointer-events-none absolute [inset-inline-end:0px] top-[0px] z-[1] h-[60px] w-[32px] bg-[linear-gradient(-90deg,rgba(255,255,255,1)_25%,rgba(255,255,255,0)_100%)] rtl:bg-[linear-gradient(90deg,rgba(255,255,255,1)_25%,rgba(255,255,255,0)_100%)]"
        ></span>
      </div>

      <div
        :id="`recommendation-apps-panel-${module.domId}`"
        role="tabpanel"
        :aria-labelledby="`recommendation-apps-tab-${module.domId}-${String(module.selection)}`"
        :class="
          module.kind === 'many'
            ? module.showGallery
              ? 'grid w-full grid-cols-1 gap-[24px] sm:grid-cols-2 lg:grid-cols-3'
              : 'flex w-full flex-col'
            : 'w-full pt-[16px]'
        "
      >
        <template v-if="module.kind === 'single'">
          <div
            v-if="module.visibleAds[0]"
            class="max-[767px]flex-col max-[767px] :h-auto flex h-[212px] min-w-0 cursor-pointer flex-row items-center gap-[30px] rounded-[8px] bg-[#FAFAFA] p-[26px] max-[767px]:items-stretch"
            :class="[TRADER_CARD_HOVER_SHADOW]"
            @click="handleAdClick(module.visibleAds[0], module.id)"
          >
            <component
              :is="getAdHref(module.visibleAds[0]) ? nuxtLinkComponent : 'div'"
              v-bind="getAdLinkProps(getAdHref(module.visibleAds[0]))"
              class="flex min-w-0 flex-1 flex-col justify-center gap-[34px]"
            >
              <div class="flex min-w-0 items-center gap-[10px]">
                <span class="relative block size-[48px] shrink-0">
                  <CommonImage
                    :src="module.visibleAds[0].appLogo"
                    :alt="`${module.visibleAds[0].appName || '应用'} 图标`"
                    class="rounded-[6px]"
                    width="48"
                    height="48"
                    loading="eager"
                  />
                  <span
                    class="pointer-events-none absolute [inset-inline-end:0px] top-[0px] z-[2] flex size-[14px] items-center justify-center rounded-se-[4px] rounded-es-[4px] bg-[rgba(5,12,31,0.46)]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      v-if="module.selection === 1"
                      src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                    <CommonImage
                      v-else
                      src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                  </span>
                </span>
                <div class="flex min-w-0 flex-1 flex-col gap-[2px]">
                  <h3 class="truncate text-[18px] leading-[24px] font-[500] text-[#3D3D3D]">
                    {{ module.visibleAds[0].appName || '' }}
                  </h3>
                  <p class="truncate text-[14px] leading-[22px] font-[400] text-[#86909C]">
                    {{ module.visibleAds[0].appTitle || '' }}
                  </p>
                </div>
              </div>
              <div class="flex min-w-0 items-center gap-[40px] max-[767px]:flex-wrap max-[767px]:gap-[16px]">
                <div class="flex min-w-[90px] flex-col justify-center gap-[12px]">
                  <span class="text-[14px] leading-[22px] text-[#86909C]">{{ t('90103') }}</span>
                  <strong class="text-[16px] leading-[22px] font-[500] text-[#3D3D3D]">{{
                    formatDownloadNum(module.visibleAds[0].downloadNum)
                  }}</strong>
                </div>
                <span
                  class="h-[52px] w-[0px] border-s-[0.5px] border-[#E5E6EB] max-[767px]:hidden"
                  aria-hidden="true"
                ></span>
                <div class="flex min-w-[90px] flex-col justify-center gap-[12px]">
                  <span class="text-[14px] leading-[22px] text-[#86909C]">{{ t('89347') }}</span>
                  <strong class="text-[16px] leading-[22px] font-[500] text-[#3D3D3D]">{{
                    readText(module.visibleAds[0].sizes) || '-'
                  }}</strong>
                </div>
                <span
                  class="h-[52px] w-[0px] border-s-[0.5px] border-[#E5E6EB] max-[767px]:hidden"
                  aria-hidden="true"
                ></span>
                <div class="flex min-w-0 flex-col justify-center gap-[12px]">
                  <span class="text-[14px] leading-[22px] text-[#86909C]">{{ t('90104') }}</span>
                  <span
                    class="flex min-w-0 items-center gap-[6px]"
                    :class="getTraderHref(module.visibleAds[0]) ? 'hover:opacity-[0.8]' : ''"
                    @click.stop.prevent="handleTraderClick(module.visibleAds[0])"
                  >
                    <CommonImage
                      v-if="module.visibleAds[0].traderIco"
                      :src="module.visibleAds[0].traderIco"
                      :alt="`${module.visibleAds[0].traderName}`"
                      class="size-[20px] shrink-0 rounded-[4px]"
                      width="20"
                      height="20"
                    />
                    <strong class="truncate text-[16px] leading-[22px] font-[500] text-[#164B71]">{{
                      module.visibleAds[0].traderName || '-'
                    }}</strong>
                    <CommonImage
                      :src="traderArrowIcon"
                      alt=""
                      width="14"
                      height="14"
                      loading="eager"
                      class="size-[14px] shrink-0 rtl:rotate-180"
                    />
                  </span>
                </div>
              </div>
            </component>

            <div
              v-if="getScreenshots(module.visibleAds[0]).length"
              dir="ltr"
              class="screenshot-scrollbar flex h-[168px] w-[474px] shrink-0 cursor-pointer touch-pan-y items-start gap-[10px] overflow-x-auto overflow-y-hidden overscroll-x-contain select-none max-[767px]:w-full"
              @click.stop
              @dragstart.prevent
              @pointerdown.stop="handleScreenshotPointerDown"
              @pointermove.stop="handleScreenshotPointerMove"
              @pointerup.stop="handleScreenshotPointerEnd"
              @pointercancel.stop="handleScreenshotPointerEnd($event, true)"
            >
              <button
                v-for="(screenshot, screenshotIndex) in getScreenshots(module.visibleAds[0])"
                :key="`${module.visibleAds[0].key}-${screenshotIndex}`"
                type="button"
                class="h-[160px] min-w-[74px] shrink-0 cursor-pointer overflow-hidden rounded-[8px] transition-opacity hover:opacity-[0.88]"
                :aria-label="`${module.visibleAds[0].appName} ${screenshotIndex + 1}`"
                @click.stop.prevent="handleScreenshotClick(screenshot, module.visibleAds[0])"
              >
                <CommonImage
                  :src="screenshot"
                  :alt="`${module.visibleAds[0].appName} ${screenshotIndex + 1}`"
                  class="size-full"
                  width="74"
                  height="160"
                  object-fit="cover"
                />
              </button>
            </div>
          </div>
        </template>

        <template v-else-if="module.kind === 'double'">
          <div class="grid w-full grid-cols-1 gap-[24px] md:grid-cols-2">
            <article
              v-for="ad in module.visibleAds.slice(0, 2)"
              :key="ad.key"
              class="-mx-[8px] flex min-w-0 flex-col gap-[12px] rounded-[8px] bg-[#fff] p-[8px]"
              :class="[TRADER_CARD_HOVER_SHADOW, getAdHref(ad) ? 'cursor-pointer' : '']"
              @click="handleAdClick(ad, module.id, $event)"
            >
              <div class="flex w-full min-w-0 flex-col gap-[12px] rounded-[8px] bg-[#FAFAFA] p-[16px]">
                <component
                  :is="getAdHref(ad) ? nuxtLinkComponent : 'div'"
                  v-bind="getAdLinkProps(getAdHref(ad))"
                  class="flex min-w-0 items-center gap-[12px] border-b-[0.5px] border-[rgba(5,21,36,0.06)] pb-[12px]"
                >
                  <span class="relative block size-[40px] shrink-0">
                    <CommonImage
                      :src="ad.appLogo"
                      :alt="`${ad.appName}`"
                      class="rounded-[4px]"
                      width="40"
                      height="40"
                      loading="eager"
                    />
                    <span
                      class="pointer-events-none absolute [inset-inline-end:0px] top-[0px] z-[2] flex size-[14px] items-center justify-center rounded-se-[4px] rounded-es-[4px] bg-[rgba(5,12,31,0.46)]"
                      aria-hidden="true"
                    >
                      <CommonImage
                        v-if="module.selection === 1"
                        src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                        width="10"
                        height="10"
                        loading="eager"
                      />
                      <CommonImage
                        v-else
                        src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                        width="10"
                        height="10"
                        loading="eager"
                      />
                    </span>
                  </span>
                  <div class="flex min-w-0 flex-1 flex-col gap-[2px]">
                    <h3 class="truncate text-[16px] leading-[24px] font-[500] text-[#292A2E]">
                      {{ ad.appName || '' }}
                    </h3>
                    <p class="truncate text-[12px] leading-[18px] font-[400] text-[#7D818A]">{{ ad.appTitle || '' }}</p>
                  </div>
                  <span
                    class="flex shrink-0 flex-col items-end gap-[4px] text-[16px] leading-[24px] font-[500] text-[#292A2E]"
                  >
                    <CommonImage :src="downloadIcon" alt="" width="16" height="16" loading="eager" />
                    {{ formatDownloadNum(ad.downloadNum) }}
                  </span>
                </component>
                <div
                  v-if="getScreenshots(ad).length"
                  dir="ltr"
                  class="screenshot-scrollbar flex h-[150px] min-w-0 cursor-pointer touch-pan-y items-start gap-[10px] overflow-x-auto overflow-y-hidden overscroll-x-contain select-none"
                  @click.stop
                  @dragstart.prevent
                  @pointerdown.stop="handleScreenshotPointerDown"
                  @pointermove.stop="handleScreenshotPointerMove"
                  @pointerup.stop="handleScreenshotPointerEnd"
                  @pointercancel.stop="handleScreenshotPointerEnd($event, true)"
                >
                  <button
                    v-for="(screenshot, screenshotIndex) in getScreenshots(ad)"
                    :key="`${ad.key}-${screenshotIndex}`"
                    type="button"
                    class="h-[142px] shrink-0 cursor-pointer overflow-hidden rounded-[4px] transition-opacity hover:opacity-[0.88]"
                    :style="{ flexBasis: 'calc((100% - 40px) / 5)' }"
                    :aria-label="`${ad.appName} ${screenshotIndex + 1}`"
                    @click.stop.prevent="handleScreenshotClick(screenshot, ad)"
                  >
                    <CommonImage
                      :src="screenshot"
                      :alt="`${ad.appName} ${screenshotIndex + 1}`"
                      class="size-full"
                      height="142"
                      object-fit="cover"
                    />
                  </button>
                </div>
              </div>
            </article>
          </div>
        </template>

        <template v-else-if="module.showGallery">
          <article
            v-for="ad in module.visibleAds.slice(0, 3)"
            :key="ad.key"
            class="-mx-[8px] flex min-w-0 flex-col gap-[12px] rounded-[8px] bg-[#fff] p-[8px]"
            :class="[TRADER_CARD_HOVER_SHADOW, getAdHref(ad) ? 'cursor-pointer' : '']"
          >
            <component
              :is="getAdHref(ad) ? nuxtLinkComponent : 'div'"
              v-bind="getAdLinkProps(getAdHref(ad))"
              class="flex h-[76px] min-w-0 items-center gap-[12px] py-[12px]"
              @click="handleAdClick(ad, module.id)"
            >
              <span class="relative block size-[52px] shrink-0">
                <CommonImage
                  :src="ad.appLogo"
                  :alt="`${ad.appName}`"
                  class="rounded-[6px]"
                  width="52"
                  height="52"
                  loading="eager"
                />
                <span
                  class="pointer-events-none absolute [inset-inline-end:0px] top-[0px] z-[2] flex size-[14px] items-center justify-center rounded-se-[4px] rounded-es-[4px] bg-[rgba(5,12,31,0.46)]"
                  aria-hidden="true"
                >
                  <CommonImage
                    v-if="module.selection === 1"
                    src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                    width="10"
                    height="10"
                    loading="eager"
                  />
                  <CommonImage
                    v-else
                    src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                    width="10"
                    height="10"
                    loading="eager"
                  />
                </span>
              </span>
              <div class="flex min-w-0 flex-1 flex-col justify-center gap-[4px]">
                <h3 class="truncate text-[16px] leading-[24px] font-[500] text-[#292A2E]">{{ ad.appName || '' }}</h3>
                <p class="truncate text-[12px] leading-[18px] font-[400] text-[#7D818A]">{{ ad.appTitle || '' }}</p>
              </div>
              <span
                class="flex shrink-0 flex-col items-end gap-[4px] text-[16px] leading-[24px] font-[500] text-[#292A2E]"
              >
                <CommonImage :src="downloadIcon" alt="" width="16" height="16" loading="eager" />
                {{ formatDownloadNum(ad.downloadNum) }}
              </span>
            </component>

            <div
              v-if="getScreenshots(ad).length"
              dir="ltr"
              class="screenshot-scrollbar flex h-[150px] min-w-0 cursor-pointer touch-pan-y items-start gap-[10px] overflow-x-auto overflow-y-hidden overscroll-x-contain select-none"
              @click.stop
              @dragstart.prevent
              @pointerdown.stop="handleScreenshotPointerDown"
              @pointermove.stop="handleScreenshotPointerMove"
              @pointerup.stop="handleScreenshotPointerEnd"
              @pointercancel.stop="handleScreenshotPointerEnd($event, true)"
            >
              <button
                v-for="(screenshot, screenshotIndex) in getScreenshots(ad)"
                :key="`${ad.key}-${screenshotIndex}`"
                type="button"
                class="h-[142px] shrink-0 cursor-pointer overflow-hidden rounded-[4px] transition-opacity hover:opacity-[0.88]"
                :style="{ flexBasis: 'calc((100% - 40px) / 5)' }"
                :aria-label="`${ad.appName} ${screenshotIndex + 1}`"
                @click.stop.prevent="handleScreenshotClick(screenshot, ad)"
              >
                <CommonImage
                  :src="screenshot"
                  :alt="`${ad.appName} ${screenshotIndex + 1}`"
                  class="size-full"
                  height="142"
                  object-fit="cover"
                />
              </button>
            </div>
          </article>
        </template>

        <template v-else>
          <ul
            v-for="(row, rowIndex) in module.rows"
            :key="rowIndex"
            class="grid min-w-0 grid-cols-1 gap-x-[20px] sm:grid-cols-2 lg:grid-cols-3"
          >
            <li
              v-for="ad in row"
              :key="ad.key"
              :class="[
                getAdHref(ad) ? '-mx-[8px] cursor-pointer rounded-[8px] bg-[#fff] p-[8px] hover:bg-[#F8F8F8]' : '',
              ]"
            >
              <component
                :is="getAdHref(ad) ? nuxtLinkComponent : 'div'"
                v-bind="getAdLinkProps(getAdHref(ad))"
                class="flex min-w-0 items-center gap-[12px] py-[16px]"
                :class="getAdHref(ad) ? '' : ''"
                @click="handleAdClick(ad, module.id)"
              >
                <span class="relative block size-[52px] shrink-0">
                  <CommonImage :src="ad.appLogo" :alt="`${ad.appName}`" class="rounded-[6px]" width="52" height="52" />
                  <span
                    class="pointer-events-none absolute [inset-inline-end:0px] top-[0px] z-[2] flex size-[14px] items-center justify-center rounded-se-[4px] rounded-es-[4px] bg-[rgba(5,12,31,0.46)]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      v-if="module.selection === 1"
                      src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                    <CommonImage
                      v-else
                      src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                  </span>
                </span>
                <span class="flex min-w-0 flex-1 flex-col justify-center gap-[4px]">
                  <span class="truncate text-[16px] leading-[24px] font-[500] text-[#292A2E]">{{
                    ad.appName || ''
                  }}</span>
                  <span class="truncate text-[12px] leading-[18px] font-[400] text-[#7D818A]">{{
                    ad.appTitle || ''
                  }}</span>
                </span>
              </component>
            </li>
          </ul>
        </template>
      </div>
    </section>
  </div>

  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-[200ms]"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-[200ms]"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isPreviewOpen"
        class="fixed inset-[0px] z-[9999] flex items-center justify-center bg-[rgba(0,0,0,0.8)] p-[32px] backdrop-blur-[2px]"
        role="dialog"
        aria-modal="true"
        :aria-label="previewImageAlt"
        @click.self="closeImagePreview"
      >
        <div class="relative">
          <button
            ref="previewCloseButton"
            type="button"
            class="absolute [inset-inline-end:-12px] top-[-12px] z-[2] flex size-[36px] cursor-pointer items-center justify-center rounded-[50%] bg-white text-[24px] leading-[24px] text-[#292A2E] shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-transform hover:scale-[1.08] rtl:[inset-inline-start:-12px] rtl:[inset-inline-end:auto]"
            aria-label="colse review"
            @click="closeImagePreview"
          >
            ×
          </button>
          <CommonImage
            :src="previewImageUrl"
            :alt="previewImageAlt"
            class="h-[80vh] max-h-[760px] w-[min(90vw,900px)] rounded-[8px] bg-white shadow-[0_16px_48px_rgba(0,0,0,0.35)]"
            loading="eager"
            object-fit="contain"
            :fallback-scale="20"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.screenshot-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #c5c8ce transparent;
}

.screenshot-scrollbar::-webkit-scrollbar {
  display: block;
  height: 2px;
}

.screenshot-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.screenshot-scrollbar::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #c5c8ce;
}
</style>
